<template>
  <div class="admin-page">
    <header class="admin-header">
      <img src="/assets/images/CHECK-APP.png" alt="Check-App" class="header-logo" />
      <span class="header-title">{{ $t('adminPortal') }}</span>
      <button @click="logout" class="btn-logout"><i class="fas fa-sign-out-alt mr-1"></i> Logout</button>
    </header>

    <main class="admin-main">
      <div class="admin-top-bar">
        <h2 class="page-title">{{ $t('pendingStructures') }}</h2>
        <div class="filter-group">
          <select v-model="statusFilter" @change="loadStructures" class="filter-select">
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-center">
        <i class="fas fa-spinner fa-spin fa-2x text-blue-400"></i>
      </div>

      <div v-else-if="structures.length === 0" class="empty-msg">
        No structures in this state.
      </div>

      <div v-else class="pending-list">
        <div v-for="s in structures" :key="s.id" class="pending-card">
          <div class="pending-top">
            <div>
              <h3 class="pending-name">{{ s.name_en || s.name_fr }}</h3>
              <p class="pending-owner">{{ s.owner_email }} · Submitted {{ formatDate(s.updated_at) }}</p>
            </div>
            <span :class="['status-badge', `status-${s.status}`]">{{ $t(s.status) }}</span>
          </div>

          <div class="pending-details">
            <div class="detail-row">
              <span><i class="fas fa-phone mr-1"></i> {{ s.phone }}</span>
              <span><i class="fas fa-envelope mr-1"></i> {{ s.email }}</span>
            </div>
            <p v-if="s.description_en" class="detail-desc">{{ s.description_en }}</p>
          </div>

          <!-- Services / Personnel counts -->
          <div class="pending-meta">
            <span class="meta-chip"><i class="fas fa-concierge-bell mr-1"></i> {{ s.services?.length || 0 }} services</span>
            <span class="meta-chip"><i class="fas fa-users mr-1"></i> {{ s.key_personnel?.length || 0 }} personnel</span>
            <span class="meta-chip"><i class="fas fa-map-marker-alt mr-1"></i> {{ s.regional_delegations?.length || 0 }} regions</span>
            <span v-if="s.signed_document" class="meta-chip meta-signed">
              <i class="fas fa-file-signature mr-1"></i> Signed doc uploaded
            </span>
          </div>

          <div v-if="s.status === 'submitted'" class="pending-actions">
            <button @click="approve(s.id)" class="btn-approve" :disabled="actionLoading === s.id">
              <i v-if="actionLoading === s.id" class="fas fa-spinner fa-spin mr-1"></i>
              <i v-else class="fas fa-check mr-1"></i> {{ $t('approveStructure') }}
            </button>
            <button @click="openReject(s.id)" class="btn-reject">
              <i class="fas fa-times mr-1"></i> {{ $t('rejectStructure') }}
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Reject modal -->
    <div v-if="showRejectModal" class="modal-overlay" @click.self="showRejectModal = false">
      <div class="modal-box">
        <h3 class="modal-title"><i class="fas fa-times-circle text-red-500 mr-2"></i>{{ $t('rejectStructure') }}</h3>
        <textarea v-model="rejectNotes" rows="4"
                  :placeholder="$t('enterRejectionNotes')" class="modal-textarea"></textarea>
        <div v-if="rejectError" class="alert-error">{{ rejectError }}</div>
        <div class="modal-actions">
          <button @click="showRejectModal = false" class="btn-cancel">Cancel</button>
          <button @click="confirmReject" class="btn-reject-confirm" :disabled="rejectLoading">
            <i v-if="rejectLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Confirm Rejection
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route  = useRoute();
const REG_API = process.env.VUE_APP_REGISTRATION_API_URL || '/api/registration';

const token = ref('');
const structures   = ref([]);
const loading      = ref(false);
const statusFilter = ref('submitted');
const actionLoading = ref(null);
const showRejectModal = ref(false);
const rejectNotes  = ref('');
const rejectError  = ref('');
const rejectLoading = ref(false);
const rejectingId  = ref(null);

onMounted(() => {
  token.value = sessionStorage.getItem('admin_portal_token') || '';
  if (!token.value) { router.replace(`/${route.params.lang}/register/admin`); return; }
  loadStructures();
});

function logout() {
  sessionStorage.removeItem('admin_portal_token');
  sessionStorage.removeItem('admin_portal_user');
  router.push(`/${route.params.lang}/register/admin`);
}

async function loadStructures() {
  loading.value = true;
  try {
    const res = await fetch(`${REG_API}/admin/structures/?status=${statusFilter.value}`, {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    if (res.status === 401) { logout(); return; }
    structures.value = await res.json();
  } catch (_) { /* network error ignored */ } finally { loading.value = false; }
}

async function approve(id) {
  actionLoading.value = id;
  try {
    const res = await fetch(`${REG_API}/admin/structures/${id}/approve/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token.value}` },
    });
    if (res.ok) loadStructures();
    else { const d = await res.json(); alert(d.error || 'Failed'); }
  } catch (e) { alert(e.message); } finally { actionLoading.value = null; }
}

function openReject(id) {
  rejectingId.value = id;
  rejectNotes.value = '';
  rejectError.value = '';
  showRejectModal.value = true;
}

async function confirmReject() {
  rejectLoading.value = true; rejectError.value = '';
  try {
    const res = await fetch(`${REG_API}/admin/structures/${rejectingId.value}/reject/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token.value}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: rejectNotes.value }),
    });
    if (res.ok) { showRejectModal.value = false; loadStructures(); }
    else { const d = await res.json(); rejectError.value = d.error || 'Failed'; }
  } catch (e) { rejectError.value = e.message; } finally { rejectLoading.value = false; }
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year:'numeric', month:'short', day:'numeric' });
}
</script>

<style scoped>
.admin-page { min-height:100vh; background:#f1f5f9; }
.admin-header { background:#0f172a; color:#fff; display:flex; align-items:center; gap:16px;
  padding:12px 24px; box-shadow:0 2px 8px rgba(0,0,0,.3); }
.header-logo { height:42px; }
.header-title { flex:1; font-weight:700; font-size:1rem; opacity:.9; }
.btn-logout { background:rgba(255,255,255,.15); border:1px solid rgba(255,255,255,.3);
  color:#fff; border-radius:8px; padding:7px 16px; cursor:pointer; font-size:.875rem; }
.admin-main { max-width:1100px; margin:0 auto; padding:28px 20px; }
.admin-top-bar { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
.page-title { font-size:1.4rem; font-weight:700; color:#0f172a; }
.filter-select { border:1.5px solid #d1d5db; border-radius:8px; padding:8px 12px; font-size:.875rem; }
.loading-center { display:flex; justify-content:center; padding:60px; }
.empty-msg { text-align:center; color:#9ca3af; padding:60px; }
.pending-list { display:flex; flex-direction:column; gap:16px; }
.pending-card { background:#fff; border-radius:14px; padding:22px;
  box-shadow:0 2px 12px rgba(0,0,0,.06); border:1px solid #e5e7eb; }
.pending-top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:12px; }
.pending-name { font-size:1.05rem; font-weight:700; color:#0f172a; margin:0; }
.pending-owner { font-size:.8rem; color:#9ca3af; margin:3px 0 0; }
.pending-details { margin-bottom:10px; }
.detail-row { display:flex; gap:20px; font-size:.875rem; color:#374151; margin-bottom:6px; }
.detail-desc { font-size:.875rem; color:#6b7280; }
.pending-meta { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.meta-chip { background:#f3f4f6; color:#6b7280; border-radius:999px; padding:3px 10px; font-size:.75rem; }
.meta-signed { background:#d1fae5; color:#065f46; }
.status-badge { border-radius:999px; padding:3px 12px; font-size:.75rem; font-weight:600; }
.status-submitted { background:#eff6ff; color:#2563eb; }
.status-approved { background:#f0fdf4; color:#16a34a; }
.status-rejected { background:#fef2f2; color:#dc2626; }
.status-draft { background:#f3f4f6; color:#6b7280; }
.pending-actions { display:flex; gap:10px; }
.btn-approve { background:#16a34a; color:#fff; border:none; border-radius:9px;
  padding:9px 20px; font-size:.875rem; font-weight:600; cursor:pointer;
  display:flex; align-items:center; transition:background .2s; }
.btn-approve:hover:not(:disabled) { background:#15803d; }
.btn-approve:disabled { opacity:.6; cursor:not-allowed; }
.btn-reject { background:#dc2626; color:#fff; border:none; border-radius:9px;
  padding:9px 20px; font-size:.875rem; font-weight:600; cursor:pointer; }
.btn-reject:hover { background:#b91c1c; }
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:1000;
  display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box { background:#fff; border-radius:14px; padding:28px; width:100%; max-width:480px;
  box-shadow:0 20px 60px rgba(0,0,0,.3); }
.modal-title { font-size:1.1rem; font-weight:700; color:#0f172a; margin-bottom:16px; }
.modal-textarea { width:100%; border:1.5px solid #d1d5db; border-radius:8px; padding:10px;
  font-size:.875rem; box-sizing:border-box; resize:vertical; }
.alert-error { background:#fef2f2; border:1px solid #fca5a5; color:#b91c1c;
  border-radius:6px; padding:8px; font-size:.8rem; margin-top:8px; }
.modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:16px; }
.btn-cancel { background:#f3f4f6; border:none; border-radius:8px; padding:9px 18px; cursor:pointer; }
.btn-reject-confirm { background:#dc2626; color:#fff; border:none; border-radius:8px;
  padding:9px 18px; font-size:.875rem; font-weight:600; cursor:pointer; }
</style>